// Self-contained "Scheduled Visit" section for the lead detail page. Shows the
// route(s) this lead is part of (the route's date = the visit date) and links to
// each route. Drops into LeadDetails with one import + one line of JSX.
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FaRoute, FaChevronRight, FaCalendarDay } from 'react-icons/fa';
import { DetailSection } from '../../../components/styled/DetailComponents';
import { getRoutesByLead } from '../routeService';
import { formatRouteDate, routeDateRelative } from '../utils';

const Row = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  background: ${p => p.theme.colors.background}; border-radius: 8px; padding: 11px 13px; cursor: pointer;
  transition: transform 0.15s ease; & + & { margin-top: 8px; }
  &:hover { transform: translateX(3px); }
  .left { min-width: 0; }
  .date { display: flex; align-items: center; gap: 7px; color: ${p => p.theme.colors.textPrimary}; font-weight: 600; font-size: 0.9rem; }
  .date svg { color: ${p => p.theme.colors.primary}; }
  .name { color: ${p => p.theme.colors.textSecondary}; font-size: 0.8rem; margin-top: 3px; }
  .chev { color: ${p => p.theme.colors.inputGray}; flex-shrink: 0; }
`;
const EmptyText = styled.div`color: ${p => p.theme.colors.textSecondary}; font-size: 0.85rem; padding: 4px 0;`;

const LeadScheduledVisits = ({ leadId }) => {
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);

  const load = useCallback(async () => {
    if (!leadId) return;
    try { setRoutes(await getRoutesByLead(leadId)); }
    catch (err) { console.error('Error loading scheduled visits:', err); }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  return (
    <DetailSection>
      <h3><FaRoute /> Scheduled Visit</h3>
      {routes.length === 0 ? (
        <EmptyText>No visit scheduled. Add this lead to a route to schedule one.</EmptyText>
      ) : (
        routes.map(rt => (
          <Row key={rt.id} onClick={() => navigate(`/routes/${rt.id}`)}>
            <div className="left">
              <div className="date">
                <FaCalendarDay size={12} />
                {rt.date ? `${routeDateRelative(rt.date)} · ${formatRouteDate(rt.date)}` : 'No date set'}
              </div>
              <div className="name">
                {rt.name}
                {/* A lead can occupy several stops on one route (different
                    addresses); the API returns the route once, so say how many. */}
                {(rt.address_ids?.length || 0) > 1 && ` · ${rt.address_ids.length} addresses`}
              </div>
            </div>
            <FaChevronRight className="chev" size={12} />
          </Row>
        ))
      )}
    </DetailSection>
  );
};

export default LeadScheduledVisits;
